
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { CheckCircle, ArrowRight, AlertCircle, Users, Calendar, Stethoscope, ChevronRight } from 'lucide-react';

interface OnboardingProps {
  onComplete: () => void;
}

export const Onboarding: React.FC<OnboardingProps> = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [hasPatients, setHasPatients] = useState(false);
  const [hasVisits, setHasVisits] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    checkUserData();
  }, [user]);
  
  const checkUserData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Check if user has patients in Supabase
      const { count: patientCount } = await supabase
        .from('patients')
        .select('*', { count: 'exact', head: true });
        
      // Check if user has visits (admin sees all, nurses see their own)
      const isAdmin = profile?.role === 'admin';
      const visitsQuery = isAdmin 
        ? supabase.from('visits').select('*', { count: 'exact', head: true })
        : supabase.from('visits').select('*', { count: 'exact', head: true }).eq('nurse_id', user.id);
        
      const { count: visitCount } = await visitsQuery;
        
      setHasPatients((patientCount || 0) > 0);
      setHasVisits((visitCount || 0) > 0);
    } catch (error) {
      console.error('Error checking user data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const isAdmin = profile?.role === 'admin';
  
  const steps = [
    {
      id: 1,
      title: 'Welcome to PCNS Connect!',
      description: isAdmin 
        ? `Hi ${profile?.full_name || 'Admin'}! Let's get your nursing platform set up.`
        : 'Welcome! Let\'s help you get started with PCNS Connect.',
      icon: <Stethoscope className="w-12 h-12 text-blue-600" />,
      action: null,
      completed: true
    },
    {
      id: 2,
      title: 'Add Your First Patient',
      description: 'Start by adding patient information to the system.',
      icon: <Users className="w-12 h-12 text-blue-600" />,
      action: () => navigate('/dashboard'),
      actionLabel: 'Add Patient',
      completed: hasPatients
    },
    {
      id: 3,
      title: 'Schedule a Visit',
      description: 'Create your first scheduled visit for a patient.',
      icon: <Calendar className="w-12 h-12 text-blue-600" />,
      action: () => navigate('/dashboard'),
      actionLabel: 'Schedule Visit',
      completed: hasVisits
    },
    {
      id: 4,
      title: 'You\'re All Set!',
      description: 'Great job! You can now use all features of PCNS Connect.',
      icon: <CheckCircle className="w-12 h-12 text-green-600" />,
      action: onComplete,
      actionLabel: 'Start Using PCNS Connect',
      completed: false
    }
  ];

  const currentStep = steps[step - 1];
  const canProceed = step === 1 || step === 4 || (step === 2 && hasPatients) || (step === 3 && hasVisits);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-50 flex items-center justify-center z-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Progress Bar */}
          <div className="flex items-center justify-between mb-8">
            {steps.map((s, i) => (
              <React.Fragment key={s.id}>
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    s.id < step ? 'bg-green-500' : 
                    s.id === step ? 'bg-blue-600' : 
                    'bg-gray-300'
                  }`}>
                    {s.id < step ? (
                      <CheckCircle className="w-6 h-6 text-white" />
                    ) : (
                      <span className="text-white font-medium">{s.id}</span>
                    )}
                  </div>
                  <span className="text-xs mt-1 text-gray-600 hidden sm:block">{s.title.split(' ')[0]}</span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 transition-colors ${
                    s.id < step ? 'bg-green-500' : 'bg-gray-300'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Content */}
          <div className="text-center mb-8">
            <div className="mb-4 flex justify-center">{currentStep.icon}</div>
            <h2 className="text-2xl font-bold mb-4">{currentStep.title}</h2>
            <p className="text-gray-600 text-lg">{currentStep.description}</p>
            
            {/* Show completion status for steps 2 and 3 */}
            {(step === 2 || step === 3) && currentStep.completed && (
              <div className="mt-4 inline-flex items-center text-green-600">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span className="font-medium">Completed!</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-center space-x-4">
            {step > 1 && step < 4 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
            )}
            
            {currentStep.action ? (
              <button
                onClick={currentStep.action}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center transition-colors"
              >
                {currentStep.actionLabel}
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed}
                className={`px-8 py-3 rounded-lg flex items-center transition-colors ${
                  canProceed 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Next
                <ChevronRight className="ml-2 h-5 w-5" />
              </button>
            )}
          </div>

          {/* Skip for admin */}
          {isAdmin && step < 4 && (
            <div className="text-center mt-6">
              <button
                onClick={onComplete}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Skip onboarding
              </button>
            </div>
          )}
        </div>

        {/* Help Info */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm text-blue-800 font-medium">Need Help?</p>
              <p className="text-sm text-blue-700 mt-1">
                {isAdmin 
                  ? 'As admin, you can add nurses, manage all patients, and view all visits across your organization.'
                  : 'Contact your administrator or check the help section for guidance.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
